import React, { useState, useEffect, useReducer, useContext } from "react";
import openSocket from "../../services/socket-io";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import GetAppIcon from "@mui/icons-material/GetApp";
import SyncIcon from "@mui/icons-material/Sync";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Menu from "@mui/material/Menu";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";

import api from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import DuplicateContactsModal from "../../components/DuplicateContactsModal";
import ImportContactsCSVModal from "../../components/ImportContactsCSVModal";

import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];
    contacts.forEach((contact) => {
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });
    return [...state, ...newContacts];
  }
  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);
    if (contactIndex !== -1) {
      state[contactIndex] = contact;
      return [...state];
    } else {
      return [contact, ...state];
    }
  }
  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) state.splice(contactIndex, 1);
    return [...state];
  }
  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  filterRow: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
    padding: "8px 8px 4px",
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  filterField: { minWidth: 160 },
  dateField: { minWidth: 150 },
  bulkBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: "6px 12px",
    background: "#FFF3E0",
    borderBottom: `2px solid #F37021`,
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover": { background: "#fafafa" },
  },
  nameCell: {
    "& .name": { fontWeight: 600, fontSize: 14, lineHeight: 1.2 },
    "& .email": { fontSize: 11, color: "#8A9096", marginTop: 1 },
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);

  // Filters
  const [filterTag, setFilterTag] = useState("");
  const [filterStartDate, setFilterStartDate] = useState("");
  const [filterEndDate, setFilterEndDate] = useState("");
  const [allTags, setAllTags] = useState([]);

  // Bulk selection
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  // Modals
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [csvImportModalOpen, setCsvImportModalOpen] = useState(false);

  // Bling sync
  const [blingSyncing, setBlingSyncing] = useState(false);

  // Export
  const [exportAnchor, setExportAnchor] = useState(null);
  const [exporting, setExporting] = useState(false);

  // WA import
  const [waConnections, setWaConnections] = useState([]);
  const [waImportOpen, setWaImportOpen] = useState(false);
  const [waImportLoading, setWaImportLoading] = useState(false);
  const [waImportWhatsappId, setWaImportWhatsappId] = useState("");

  useEffect(() => {
    if (user.profile === "admin" || user.profile === "supervisor") {
      api.get("/whatsapp/").then(({ data }) => {
        setWaConnections(data.filter(w => w.status === "CONNECTED"));
      }).catch(() => {});
    }
  }, [user.profile]);

  // Baixa a agenda no formato escolhido, respeitando o que está filtrado na
  // tela — ou só os contatos marcados, quando há seleção. O arquivo vem do
  // backend como blob (a rota exige o header de autenticação, então não dá
  // para apontar um <a href> direto para ela).
  const handleExport = async (format) => {
    setExportAnchor(null);
    setExporting(true);
    try {
      const params = { format };
      if (selectedIds.size > 0) {
        params.ids = Array.from(selectedIds).join(",");
      } else {
        if (searchParam) params.searchParam = searchParam;
        if (filterTag) params.tagName = filterTag;
        if (filterStartDate) params.createdAtStart = filterStartDate;
        if (filterEndDate) params.createdAtEnd = filterEndDate;
      }

      const { data, headers } = await api.get("/contacts/export", {
        params,
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `contatos-${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      const total = headers?.["x-total-contacts"];
      toast.success(total ? `${total} contato(s) exportado(s).` : "Exportação concluída.");
    } catch (err) {
      toastError(err);
    }
    setExporting(false);
  };

  const handleWaImport = async () => {
    if (!waImportWhatsappId) { toast.error("Selecione uma conexão."); return; }
    setWaImportLoading(true);
    try {
      const { data } = await api.post("/contacts/import-wa", { whatsappId: +waImportWhatsappId });
      toast.success(`Importação concluída: ${data.created} criados, ${data.skipped} ignorados (de ${data.total} contatos).`);
      setWaImportOpen(false);
    } catch (err) {
      toastError(err);
    }
    setWaImportLoading(false);
  };

  useEffect(() => {
    api.get("/tags/all").then(({ data }) => setAllTags(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setSelectedIds(new Set());
  }, [searchParam, filterTag, filterStartDate, filterEndDate]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const params = { searchParam, pageNumber };
          if (filterTag) params.tagName = filterTag;
          if (filterStartDate) params.createdAtStart = filterStartDate;
          if (filterEndDate) params.createdAtEnd = filterEndDate;
          const { data } = await api.get("/contacts/", { params });
          dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
          setHasMore(data.hasMore);
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber, filterTag, filterStartDate, filterEndDate]);

  useEffect(() => {
    const socket = openSocket();
    socket.on("contact", (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
        setSelectedIds((prev) => { const next = new Set(prev); next.delete(+data.contactId); return next; });
      }
    });
    return () => {
      socket.off("contact");
      socket.disconnect();
    };
  }, []);

  const handleSearch = (event) => { setSearchParam(event.target.value.toLowerCase()); };

  const handleOpenContactModal = () => { setSelectedContactId(null); setContactModalOpen(true); };
  const handleCloseContactModal = () => { setSelectedContactId(null); setContactModalOpen(false); };

  const handleSaveTicket = async (contactId) => {
    if (!contactId) return;
    setLoading(true);
    try {
      const { data: ticket } = await api.post("/tickets", { contactId, userId: user?.id, status: "open" });
      navigate(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  const hadleEditContact = (contactId) => { setSelectedContactId(contactId); setContactModalOpen(true); };

  const handleToggleMute = async (contact) => {
    try {
      await api.post(`/contacts/${contact.id}/mute`, { muted: !contact.muted });
      toast.success(contact.muted ? "Grupo desmutado." : "Grupo mutado — não vai mais aparecer na lista geral.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
  };

  const handleimportContact = async () => {
    try {
      await api.post("/contacts/import");
      toast.success("Importação iniciada — contatos aparecerão automaticamente nos próximos minutos.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleBlingSync = async () => {
    if (!window.confirm("Sincronizar todos os contatos sem vínculo Bling? Pode demorar alguns minutos.")) return;
    setBlingSyncing(true);
    try {
      const { data } = await api.post("/bling/contacts/sync-all");
      toast.success(`Sync Bling: ${data.linked} vinculados, ${data.skipped} sem correspondência (de ${data.total} sem vínculo).`);
    } catch (err) {
      toastError(err);
    } finally {
      setBlingSyncing(false);
    }
  };

  const handleBulkDelete = async () => {
    try {
      const ids = Array.from(selectedIds);
      await api.delete("/contacts/bulk", { data: { ids } });
      toast.success(`${ids.length} contatos excluídos.`);
      setSelectedIds(new Set());
    } catch (err) {
      toastError(err);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === contacts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };

  const loadMore = () => { setPageNumber((prevState) => prevState + 1); };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) loadMore();
  };

  return (
    <MainContainer>
      <ContactModal open={contactModalOpen} onClose={handleCloseContactModal} contactId={selectedContactId} />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : i18n.t("contacts.confirmationModal.importTitlte")
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() => deletingContact ? handleDeleteContact(deletingContact.id) : handleimportContact()}
      >
        {deletingContact
          ? i18n.t("contacts.confirmationModal.deleteMessage")
          : i18n.t("contacts.confirmationModal.importMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Excluir ${selectedIds.size} contato(s)?`}
        open={bulkConfirmOpen}
        onClose={setBulkConfirmOpen}
        onConfirm={handleBulkDelete}
      >
        Esta ação não pode ser desfeita. Tickets vinculados a esses contatos podem ficar sem contato.
      </ConfirmationModal>
      <DuplicateContactsModal open={duplicateModalOpen} onClose={() => setDuplicateModalOpen(false)} />
      <ImportContactsCSVModal open={csvImportModalOpen} onClose={() => setCsvImportModalOpen(false)} />

      <MainHeader>
        <Title>{i18n.t("contacts.title")}</Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
              endAdornment: searchParam ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchParam("")} edge="end">
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          {(user.profile === "admin" || user.profile === "supervisor") && (
            <Tooltip title="Vincular automaticamente contatos sem Bling pelo número de telefone">
              <Button variant="outlined"
                startIcon={blingSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
                onClick={handleBlingSync}
                disabled={blingSyncing}
                style={{ borderColor: "#1565c0", color: "#1565c0" }}>
                Sync Bling
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Verificar contatos duplicados por número">
            <Button variant="outlined" startIcon={<FileCopyIcon />} onClick={() => setDuplicateModalOpen(true)}
              style={{ borderColor: "#273A74", color: "#273A74" }}>
              Duplicatas
            </Button>
          </Tooltip>
          {user.profile === "admin" && (
            <>
              <Tooltip title="Baixar a agenda (respeita os filtros da tela)">
                <Button variant="outlined"
                  startIcon={exporting ? <CircularProgress size={16} /> : <SaveAltIcon />}
                  onClick={(e) => setExportAnchor(e.currentTarget)}
                  disabled={exporting}
                  style={{ borderColor: "#27AE60", color: "#27AE60" }}>
                  Exportar
                </Button>
              </Tooltip>
              <Menu anchorEl={exportAnchor} open={Boolean(exportAnchor)} onClose={() => setExportAnchor(null)}>
                <MenuItem onClick={() => handleExport("csv")}>
                  Planilha CSV (Excel)
                </MenuItem>
                <MenuItem onClick={() => handleExport("vcf")}>
                  vCard (.vcf) — agenda do celular / transmissão
                </MenuItem>
              </Menu>
            </>
          )}
          <Tooltip title="Importar contatos de um arquivo CSV">
            <Button variant="outlined" startIcon={<GetAppIcon />} onClick={() => setCsvImportModalOpen(true)}
              style={{ borderColor: "#F37021", color: "#F37021" }}>
              CSV
            </Button>
          </Tooltip>
          {waConnections.length > 0 && (
            <Tooltip title="Importar contatos diretamente do WhatsApp">
              <Button variant="outlined" startIcon={<WhatsAppIcon />} onClick={() => setWaImportOpen(true)}
                style={{ borderColor: "#25D366", color: "#25D366" }}>
                WhatsApp
              </Button>
            </Tooltip>
          )}
          <Button variant="contained" color="primary" onClick={handleOpenContactModal}>
            {i18n.t("contacts.buttons.add")}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined" onScroll={handleScroll}>
        {/* Filter row */}
        <div className={classes.filterRow}>
          <FormControl variant="outlined" size="small" className={classes.filterField}>
            <InputLabel>Tag</InputLabel>
            <Select value={filterTag} onChange={(e) => setFilterTag(e.target.value)} label="Tag">
              <MenuItem value="">Todas</MenuItem>
              {allTags.map((t) => (
                <MenuItem key={t.name} value={t.name}>
                  <Chip size="small" label={t.name} style={{ backgroundColor: t.color, color: "#fff", fontSize: 11 }} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Criado de" type="date" size="small" variant="outlined"
            className={classes.dateField} InputLabelProps={{ shrink: true }}
            value={filterStartDate} onChange={(e) => setFilterStartDate(e.target.value)} />
          <TextField label="Até" type="date" size="small" variant="outlined"
            className={classes.dateField} InputLabelProps={{ shrink: true }}
            value={filterEndDate} onChange={(e) => setFilterEndDate(e.target.value)} />
          {(filterTag || filterStartDate || filterEndDate) && (
            <Button size="small" onClick={() => { setFilterTag(""); setFilterStartDate(""); setFilterEndDate(""); }}>
              Limpar filtros
            </Button>
          )}
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className={classes.bulkBar}>
            <Typography variant="body2" style={{ fontWeight: 600 }}>
              {selectedIds.size} contato(s) selecionado(s)
            </Typography>
            <Button size="small" variant="contained" style={{ backgroundColor: "#E74C3C", color: "#fff" }}
              startIcon={<DeleteOutlineIcon />} onClick={() => setBulkConfirmOpen(true)}>
              Excluir selecionados
            </Button>
            <Button size="small" onClick={() => setSelectedIds(new Set())}>Cancelar</Button>
          </div>
        )}

        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedIds.size > 0 && selectedIds.size < contacts.length}
                  checked={contacts.length > 0 && selectedIds.size === contacts.length}
                  onChange={toggleSelectAll}
                  size="small"
                />
              </TableCell>
              <TableCell padding="checkbox" />
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell>{i18n.t("contacts.table.whatsapp")}</TableCell>
              <TableCell>Tags</TableCell>
              <TableCell align="right" style={{ width: 120 }}>{i18n.t("contacts.table.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => (
              <TableRow
                key={contact.id}
                selected={selectedIds.has(contact.id)}
                className={classes.clickableRow}
                onClick={() => hadleEditContact(contact.id)}
              >
                <TableCell padding="checkbox" onClick={e => e.stopPropagation()}>
                  <Checkbox size="small" checked={selectedIds.has(contact.id)} onChange={() => toggleSelect(contact.id)} />
                </TableCell>
                <TableCell style={{ paddingRight: 0 }}>
                  <Avatar src={contact.profilePicUrl} style={{ width: 42, height: 42 }} />
                </TableCell>
                <TableCell className={classes.nameCell}>
                  <div className="name">{contact.name}</div>
                  {contact.email && <div className="email">{contact.email}</div>}
                </TableCell>
                <TableCell style={{ fontSize: 13, color: "#444" }}>{contact.number}</TableCell>
                <TableCell>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {contact.tags?.map((tag) => (
                      <Chip key={tag.id} size="small" label={tag.name}
                        style={{ backgroundColor: tag.color, color: "#fff", fontSize: 10 }} />
                    ))}
                  </div>
                </TableCell>
                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  {contact.isGroup && (
                    <Tooltip title={contact.muted ? "Desmutar grupo" : "Mutar grupo (sumir da lista geral)"}>
                      <IconButton size="small" onClick={() => handleToggleMute(contact)}>
                        {contact.muted
                          ? <VolumeOffIcon fontSize="small" style={{ color: "#E74C3C" }} />
                          : <VolumeUpIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Iniciar conversa">
                    <IconButton size="small" onClick={() => handleSaveTicket(contact.id)}>
                      <WhatsAppIcon fontSize="small" style={{ color: "#25D366" }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Editar contato">
                    <IconButton size="small" onClick={() => hadleEditContact(contact.id)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Can
                    role={user.profile}
                    perform="contacts-page:deleteContact"
                    yes={() => (
                      <Tooltip title="Excluir contato">
                        <IconButton size="small" onClick={() => { setConfirmOpen(true); setDeletingContact(contact); }}>
                          <DeleteOutlineIcon fontSize="small" style={{ color: "#E74C3C" }} />
                        </IconButton>
                      </Tooltip>
                    )}
                  />
                </TableCell>
              </TableRow>
            ))}
            {loading && <TableRowSkeleton avatar columns={4} />}
          </TableBody>
        </Table>
      </Paper>

      {/* Dialog: Importar do WhatsApp */}
      <Dialog open={waImportOpen} onClose={() => setWaImportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Importar contatos do WhatsApp</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 12 }}>
            Selecione a conexão WhatsApp. Todos os contatos do dispositivo serão importados para o sistema.
          </Typography>
          <Select
            fullWidth
            value={waImportWhatsappId}
            onChange={e => setWaImportWhatsappId(e.target.value)}
            displayEmpty
          >
            <MenuItem value="" disabled>Selecione uma conexão</MenuItem>
            {waConnections.map(w => (
              <MenuItem key={w.id} value={w.id}>{w.name}</MenuItem>
            ))}
          </Select>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWaImportOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleWaImport}
            disabled={waImportLoading}
            startIcon={waImportLoading ? <CircularProgress size={18} /> : <WhatsAppIcon />}
          >
            Importar
          </Button>
        </DialogActions>
      </Dialog>
    </MainContainer>
  );
};

export default Contacts;
